import React, { useState, useRef } from 'react';
import { Users, Upload, Trash2, X, Phone, Building2, MapPin, CheckCircle, FileText, Search, Loader2, AlertTriangle, Eye } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';
import { StatusBadge } from './shared';

export function LeadsTab({ leads, segments, selectedSegment, selectedStatus, searchTerm, 
                    onSegmentChange, onStatusChange, onSearchChange, onImport, onDelete, onDeleteBySegment, loading, token }) {
  const [importSegment, setImportSegment] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = React.useRef(null);

  const API_URL = getBackendUrl() || '';

  const filteredLeads = leads.filter(lead => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchPhone = lead.phone?.toLowerCase().includes(search);
      const matchName = lead.business_name?.toLowerCase().includes(search);
      const matchCity = lead.city?.toLowerCase().includes(search);
      if (!matchPhone && !matchName && !matchCity) return false;
    }
    return true;
  });

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/prospection/leads/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao baixar template');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_leads_prospecao.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao baixar template: ' + err.message);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !importSegment) return;
    e.target.value = '';
    
    // Fazer preview antes de importar
    setPreviewLoading(true);
    setPreviewFile(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/prospection/leads/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);
      
      setPreviewData(data);
      setShowPreview(true);
    } catch (err) {
      alert('Erro ao processar arquivo: ' + err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmImport = () => {
    if (previewFile && importSegment) {
      onImport(previewFile, importSegment);
      setShowPreview(false);
      setPreviewData(null);
      setPreviewFile(null);
    }
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setPreviewFile(null);
  };

  return (
    <div className="space-y-4">
      {/* Filtros e Ações */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm w-48"
              data-testid="leads-search-input"
            />
          </div>
          
          <select
            value={selectedSegment}
            onChange={(e) => onSegmentChange(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            data-testid="leads-segment-filter"
          >
            <option value="">Todos os segmentos</option>
            {segments.map(seg => (
              <option key={seg.id} value={seg.id}>{seg.name}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            data-testid="leads-status-filter"
          >
            <option value="">Todos os status</option>
            <option value="new">Novos</option>
            <option value="contacted">Contatados</option>
            <option value="qualified">Qualificados</option>
            <option value="interested">Interessados</option>
            <option value="converted">Convertidos</option>
            <option value="rejected">Rejeitados</option>
            <option value="bot_detected">Bots</option>
          </select>
        </div>

        {/* Importar */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
            data-testid="leads-download-template-btn"
          >
            <FileText size={16} />
            Baixar Template
          </button>
          <select
            value={importSegment}
            onChange={(e) => setImportSegment(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            data-testid="leads-import-segment-select"
          >
            <option value="">Segmento destino...</option>
            {segments.map(seg => (
              <option key={seg.id} value={seg.id}>{seg.name}</option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!importSegment || loading || previewLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            data-testid="leads-import-btn"
          >
            {previewLoading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {previewLoading ? 'Analisando...' : 'Importar CSV/TXT'}
          </button>
        </div>
      </div>

      {/* Ação de exclusão em massa */}
      {onDeleteBySegment && selectedSegment && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0" />
          <span className="text-red-300 text-sm flex-1">
            Segmento selecionado: <strong>{segments.find(s => s.id === selectedSegment)?.name || selectedSegment}</strong>
          </span>
          <button
            onClick={() => onDeleteBySegment(selectedSegment, segments.find(s => s.id === selectedSegment)?.name)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="leads-delete-segment-btn"
          >
            <Trash2 size={16} />
            Excluir Todos do Segmento
          </button>
        </div>
      )}

      {/* Info do formato */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
        <strong>Formatos aceitos:</strong>
        <ul className="mt-1 ml-4 list-disc space-y-0.5">
          <li><strong>Lista simples</strong> (.txt ou .csv) — um telefone por linha: <code className="bg-gray-800 px-1 rounded">5534999999999</code></li>
          <li><strong>CSV completo</strong> — com header: <code className="bg-gray-800 px-1 rounded">phone,business_name,city,state,source,notes</code></li>
        </ul>
      </div>

      {/* Modal de Preview */}
      {showPreview && previewData && (
        <div className="bg-gray-800 border border-gray-600 rounded-xl p-5 space-y-4" data-testid="leads-import-preview">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Eye size={20} />
              Preview da Importação
            </h3>
            <button onClick={handleCancelPreview} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          
          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-900/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-white">{previewData.total_lines}</div>
              <div className="text-xs text-gray-400">Linhas no arquivo</div>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-400">{previewData.valid}</div>
              <div className="text-xs text-gray-400">Novos leads</div>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-400">{previewData.duplicates}</div>
              <div className="text-xs text-gray-400">Duplicados</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-400">{previewData.invalid}</div>
              <div className="text-xs text-gray-400">Inválidos</div>
            </div>
          </div>

          {/* Amostras */}
          {previewData.preview_valid.length > 0 && (
            <div>
              <div className="text-sm font-medium text-green-400 mb-1">Números válidos (amostra):</div>
              <div className="flex flex-wrap gap-2">
                {previewData.preview_valid.map((phone, i) => (
                  <span key={i} className="px-2 py-1 bg-green-500/10 border border-green-500/30 rounded text-xs text-green-300 font-mono">
                    {phone}
                  </span>
                ))}
                {previewData.valid > 20 && (
                  <span className="px-2 py-1 text-xs text-gray-500">+{previewData.valid - 20} mais...</span>
                )}
              </div>
            </div>
          )}
          
          {previewData.preview_invalid.length > 0 && (
            <div>
              <div className="text-sm font-medium text-red-400 mb-1">Números inválidos:</div>
              <div className="flex flex-wrap gap-2">
                {previewData.preview_invalid.map((phone, i) => (
                  <span key={i} className="px-2 py-1 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300 font-mono">
                    {phone}
                  </span>
                ))}
              </div>
            </div>
          )}

          {previewData.preview_duplicates.length > 0 && (
            <div>
              <div className="text-sm font-medium text-yellow-400 mb-1">Já existentes:</div>
              <div className="flex flex-wrap gap-2">
                {previewData.preview_duplicates.map((phone, i) => (
                  <span key={i} className="px-2 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-300 font-mono">
                    {phone}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Botões de ação */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleConfirmImport}
              disabled={previewData.valid === 0 || loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              data-testid="leads-confirm-import-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
              Confirmar Importação ({previewData.valid} leads)
            </button>
            <button
              onClick={handleCancelPreview}
              className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              data-testid="leads-cancel-import-btn"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de Leads */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
              <th className="pb-3 pr-4">Telefone</th>
              <th className="pb-3 pr-4">Estabelecimento</th>
              <th className="pb-3 pr-4">Segmento</th>
              <th className="pb-3 pr-4">Cidade</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3 pr-4">Criado em</th>
              <th className="pb-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map(lead => (
              <tr key={lead.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-2 text-white">
                    <Phone size={14} className="text-gray-500" />
                    {lead.phone}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-2 text-gray-300">
                    <Building2 size={14} className="text-gray-500" />
                    {lead.business_name || '-'}
                  </span>
                </td>
                <td className="py-3 pr-4 text-gray-400">{lead.segment_name}</td>
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-1 text-gray-400">
                    <MapPin size={14} />
                    {lead.city || '-'}{lead.state ? `, ${lead.state}` : ''}
                  </span>
                </td>
                <td className="py-3 pr-4"><StatusBadge status={lead.status} /></td>
                <td className="py-3 pr-4 text-gray-500 text-sm">
                  {lead.created_at ? new Date(lead.created_at).toLocaleDateString('pt-BR') : '-'}
                </td>
                <td className="py-3">
                  <button
                    onClick={() => onDelete(lead.id)}
                    className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
                    title="Excluir"
                    data-testid={`leads-delete-${lead.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredLeads.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            <Users size={48} className="mx-auto mb-3 opacity-50" />
            <p>Nenhum lead encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
}
